import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import "./styles/globals.css";
import * as Sentry from "@sentry/react";
import { reloadOnce } from "./presentation/utils/lazyWithRetry";
import {
  installDomResilience,
  type NodePrototypeLike,
} from "./presentation/utils/domResilience";
import {
  describeErrorEvent,
  isForeignScriptError,
} from "./presentation/utils/sentryEventFilters";

// Vite emits this when a preloaded chunk/CSS request fails — after a deploy
// this means the tab holds a stale index.html; recover with a one-shot reload.
window.addEventListener("vite:preloadError", (event) => {
  if (reloadOnce()) {
    event.preventDefault();
  }
});

// Browser translation and extensions rewrite React-owned DOM; without this a
// later commit throws NotFoundError and the error boundary blanks the screen.
if (typeof Node !== "undefined") {
  installDomResilience(Node.prototype as unknown as NodePrototypeLike);
}

Sentry.init({
  dsn: "https://20726462d468594b3450b3a4acfcdaf2@o4511065550225408.ingest.de.sentry.io/4511065552388176",
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "production",
  release: __SENTRY_RELEASE__,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Browser-extension code is never ours to fix.
  denyUrls: [
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:/i,
    /extensions\//i,
  ],
  beforeSend(event) {
    // Scripts the browser injects (Chrome iOS translate, in-app browser
    // bridges) throw with no script URL at all. Keep a breadcrumb so the
    // suppression stays visible on any real error that follows.
    if (isForeignScriptError(event)) {
      Sentry.addBreadcrumb({
        category: "foreign_script_error.suppressed",
        level: "warning",
        message: describeErrorEvent(event),
        data: { page: window.location.pathname },
      });
      return null;
    }
    return event;
  },
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
  // Enable logs to be sent to Sentry
  enableLogs: true,
});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
